import React, { useState } from 'react';
import { branding } from '../constants/branding';
import { apiService } from '../services/apiService';
import { formatAmount } from '../utils/currency';

interface IGamingAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  type: 'deposit' | 'withdraw';
  accounts: any[];
  igamingAccount: any;
}

const IGamingAccountModal: React.FC<IGamingAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  type,
  accounts,
  igamingAccount
}) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);

    try {
      // Build query string for @RequestParam parameters
      const params = new URLSearchParams();
      if (type === 'deposit') {
        params.append('sourceAccountId', selectedAccountId);
      } else {
        params.append('destinationAccountId', selectedAccountId);
      }
      params.append('amount', amount);

      const endpoint = type === 'deposit' 
        ? `/api/igaming-account/deposit?${params.toString()}`
        : `/api/igaming-account/withdraw?${params.toString()}`;

      const response = await apiService.post(endpoint);

      if (response.success) {
        onSuccess();
        setAmount('');
        setSelectedAccountId('');
      } else {
        setError(response.error || `${type === 'deposit' ? 'Deposit' : 'Withdrawal'} failed`);
      }
    } catch (err: any) {
      setError(err.message || `${type === 'deposit' ? 'Deposit' : 'Withdrawal'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = accounts.find(acc => acc.id.toString() === selectedAccountId);

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h3 style={modalHeaderStyle}>
          {type === 'deposit' ? 'Deposit to iGaming Account' : 'Withdraw from iGaming Account'}
        </h3>

        {igamingAccount && (
          <div style={{
            background: '#faf5ff',
            padding: 16,
            borderRadius: 8,
            marginBottom: 20,
            border: '2px solid #8b5cf6'
          }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Current iGaming Balance</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6' }}>
              {formatAmount(igamingAccount.balance, igamingAccount.currency)}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor }}>
              {type === 'deposit' ? 'From Account' : 'To Account'}
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14
              }}
              required
            >
              <option value="">Select an account</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.type} - {formatAmount(account.balance, account.currency)}
                </option>
              ))}
            </select>
          </div>

          {selectedAccount && (
            <div style={{
              background: '#f9fafb',
              padding: 12,
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 13,
              color: '#666'
            }}>
              Available Balance: <strong>{formatAmount(selectedAccount.balance, selectedAccount.currency)}</strong>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor }}>
              Amount
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14
              }}
              required
            />
          </div>

          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: 12,
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 14
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: 'white',
                color: branding.textColor,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: 12,
                border: 'none',
                borderRadius: 8,
                background: loading ? '#9ca3af' : branding.primaryColor,
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {loading ? 'Processing...' : (type === 'deposit' ? 'Deposit' : 'Withdraw')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: 20
};

const modalContentStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 16,
  padding: 32,
  maxWidth: 500,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const modalHeaderStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: branding.textColor,
  marginBottom: 24
};

export default IGamingAccountModal;

